import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND } from "../data/content";

export default function Showreel() {
  const [playing, setPlaying] = useState(false);
  return (
    <section id="showreel" className="vw-section relative" data-testid="vw-showreel">
      <div className="vw-container">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="vw-eyebrow mb-6">SHOWREEL · 02:25</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight max-w-[14ch]">
              Watch what <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">150 artists</span> ship.
            </h2>
          </div>
          <p className="max-w-md text-[var(--vw-text-dim)] leading-relaxed">
            Selected sequences from features, series, and short-form work delivered across the last four years — cut for producers, not for social feeds.
          </p>
        </div>

        <div className="relative group vw-cinema w-full border border-[var(--vw-border-strong)] overflow-hidden bg-black">
          <AnimatePresence mode="wait">
            {!playing ? (
              <motion.button
                key="poster"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setPlaying(true)}
                className="absolute inset-0 w-full h-full cursor-pointer"
                data-testid="vw-showreel-play"
                aria-label="Play showreel"
              >
                {/* CSS-only cinematic poster — no external dependency */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#08131f] via-[#0a1a2b] to-black" />
                <div className="absolute inset-0 opacity-40" style={{
                  background: "radial-gradient(ellipse 80% 50% at 30% 30%, rgba(0,229,255,0.35) 0%, transparent 60%), radial-gradient(ellipse 60% 60% at 80% 70%, rgba(43,108,255,0.28) 0%, transparent 55%)",
                }} />
                <svg className="absolute inset-0 w-full h-full opacity-[0.14] group-hover:opacity-25 transition-opacity" viewBox="0 0 1200 500" preserveAspectRatio="xMidYMid slice" aria-hidden>
                  <defs>
                    <linearGradient id="shrLine" x1="0" y1="0" x2="1200" y2="500" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#00E5FF"/>
                      <stop offset="100%" stopColor="#2B6CFF"/>
                    </linearGradient>
                  </defs>
                  {/* film-strip perforations */}
                  {Array.from({ length: 24 }).map((_, i) => (
                    <rect key={`t${i}`} x={20 + i * 50} y={18} width="24" height="10" fill="none" stroke="url(#shrLine)" strokeWidth="0.8"/>
                  ))}
                  {Array.from({ length: 24 }).map((_, i) => (
                    <rect key={`b${i}`} x={20 + i * 50} y={472} width="24" height="10" fill="none" stroke="url(#shrLine)" strokeWidth="0.8"/>
                  ))}
                  {/* topographic contours */}
                  <path d="M0 220 Q 300 180 600 240 T 1200 200" stroke="url(#shrLine)" strokeWidth="0.8" fill="none"/>
                  <path d="M0 300 Q 300 340 600 280 T 1200 320" stroke="url(#shrLine)" strokeWidth="0.8" fill="none"/>
                  <path d="M0 380 Q 300 360 600 400 T 1200 360" stroke="url(#shrLine)" strokeWidth="0.6" fill="none"/>
                </svg>
                <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="relative w-24 h-24 md:w-32 md:h-32 grid place-items-center border-2 border-[var(--vw-cyan)] rounded-full bg-black/40 backdrop-blur-sm vw-pulse group-hover:bg-[var(--vw-cyan)] transition-colors">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" className="text-[var(--vw-cyan)] group-hover:text-black transition-colors ml-2">
                      <path d="M5 3v18l16-9L5 3z"/>
                    </svg>
                  </div>
                  <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-text)] flex items-center gap-3">
                    <span className="w-2 h-2 bg-[var(--vw-cyan)] vw-blink" />
                    PLAY · SHOWREEL · 2020
                  </div>
                </div>

                {/* Corners */}
                {[
                  "top-4 left-4 border-t-2 border-l-2",
                  "top-4 right-4 border-t-2 border-r-2",
                  "bottom-4 left-4 border-b-2 border-l-2",
                  "bottom-4 right-4 border-b-2 border-r-2",
                ].map((c) => (
                  <span key={c} className={`absolute w-8 h-8 border-[var(--vw-cyan)] ${c}`} />
                ))}
              </motion.button>
            ) : (
              <motion.div
                key="player"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0"
              >
                <iframe
                  src="https://player.vimeo.com/video/442776059?autoplay=1&title=0&byline=0&portrait=0"
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="VawWorld Showreel"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Filmstrip stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--vw-border)]">
          {[
            ["FORMATS", "4K / 2K / HD"],
            ["COLOR SPACE", "ACES / REC.709"],
            ["MASTER", "DCP · IMF · PRORES"],
            ["AUDIO", "5.1 / ATMOS"],
          ].map(([k, v]) => (
            <div key={k} className="bg-[var(--vw-bg)] p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-1">{k}</div>
              <div className="font-mono text-sm text-[var(--vw-text)]">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
